import { useState } from 'react';
import { useViewer } from '@/features/session';
import type { CodexConnectionData } from '@/shared/types/workspace';
import { useDisconnectCodex } from './use-disconnect-codex';
import { useRotateCodexSecret } from './use-rotate-codex-secret';
import { useStartCodexConnection } from './use-start-codex-connection';

type UseCodexCardResult = {
  isOn: boolean;
  isWaiting: boolean;
  isOff: boolean;
  isConfirming: boolean;
  isRotated: boolean;
  isStarting: boolean;
  isChecking: boolean;
  isRotating: boolean;
  isDisconnecting: boolean;
  connect: () => Promise<void>;
  checkAgain: () => Promise<void>;
  rotate: () => Promise<void>;
  askDisconnect: () => void;
  keepConnected: () => void;
  disconnect: () => Promise<void>;
};

export function useCodexCard(connection: CodexConnectionData): UseCodexCardResult {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isRotated, setIsRotated] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const { refreshViewer } = useViewer();
  const { startCodexConnection, isStarting } = useStartCodexConnection();
  const { rotateCodexSecret, isRotating } = useRotateCodexSecret();
  const { disconnectCodex, isDisconnecting } = useDisconnectCodex();

  return {
    isOn: connection.status === 'CONNECTED',
    isWaiting: connection.status === 'WAITING',
    isOff: connection.status === 'NOT_CONNECTED',
    isConfirming,
    isRotated,
    isStarting,
    isChecking,
    isRotating,
    isDisconnecting,
    connect: startCodexConnection,
    checkAgain: async () => {
      setIsChecking(true);
      try {
        await refreshViewer();
      } finally {
        setIsChecking(false);
      }
    },
    rotate: async () => {
      await rotateCodexSecret();
      setIsRotated(true);
    },
    askDisconnect: () => setIsConfirming(true),
    keepConnected: () => setIsConfirming(false),
    disconnect: async () => {
      await disconnectCodex();
      setIsConfirming(false);
      setIsRotated(false);
    },
  };
}
